// frontend/src/pages/AccountSuspended.js
// Página para cuentas suspendidas por falta de pago

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './AccountSuspended.css';
import { FaExclamationTriangle, FaCreditCard, FaEnvelope, FaPhone, FaCalendar } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';

const AccountSuspended = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [accountInfo, setAccountInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAccountInfo();
  }, []);

  const loadAccountInfo = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const token = localStorage.getItem('token');

      if (!user || !token) {
        navigate('/login');
        return;
      }

      // Obtener información de la cuenta desde el backend
      const response = await fetch(API_ENDPOINTS.accountSuspensionInfo, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAccountInfo(data.data);
      }
    } catch (error) {
      console.error('Error cargando información:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = () => {
    // Redirigir a planes con información de reactivación
    navigate('/planes', {
      state: {
        reactivating: true,
        fromSuspension: true
      }
    });
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:soporte@urbanq.com?subject=Reactivación de Cuenta';
  };

  if (loading) {
    return (
      <div className="account-suspended-page">
        <div className="urbanq-loading-spinner">
          <div className="urbanq-spinner"></div>
          <p>Cargando información...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-suspended-page">
      <div className="urbanq-suspended-container">
        <div className="urbanq-suspended-content">
          
          {/* Icono de advertencia */}
          <div className="urbanq-suspended-icon">
            <div className="urbanq-warning-circle">
              <FaExclamationTriangle />
            </div>
          </div>

          {/* Mensaje principal */}
          <div className="urbanq-suspended-message">
            <h1 className="urbanq-suspended-title">Cuenta Suspendida</h1>
            <p className="urbanq-suspended-subtitle">
              Tu suscripción ha expirado y tu cuenta está temporalmente suspendida
            </p>
          </div>

          {/* Card con información */}
          <div className="urbanq-status-card">
            <div className="urbanq-status-icon">
              <FaCalendar />
            </div>
            <div className="urbanq-status-info">
              <h3>Detalles de tu Suscripción</h3>
              
              {accountInfo?.subscription ? (
                <>
                  <div className="urbanq-status-detail">
                    <span className="urbanq-status-label">Plan:</span>
                    <span className="urbanq-status-value">
                      {accountInfo.subscription.plan_name}
                    </span>
                  </div>
                  <div className="urbanq-status-detail">
                    <span className="urbanq-status-label">Última fecha de pago:</span>
                    <span className="urbanq-status-value">
                      {accountInfo.subscription.last_payment_date || 'N/A'}
                    </span>
                  </div>
                  <div className="urbanq-status-detail">
                    <span className="urbanq-status-label">Fecha de expiración:</span>
                    <span className="urbanq-status-value">
                      {accountInfo.subscription.expiration_date}
                    </span>
                  </div>
                  <div className="urbanq-status-detail">
                    <span className="urbanq-status-label">Estado:</span>
                    <span className="urbanq-status-badge urbanq-badge-suspended">
                      Suspendida
                    </span>
                  </div>
                  <div className="urbanq-status-detail">
                    <span className="urbanq-status-label">Monto pendiente:</span>
                    <span className="urbanq-status-value urbanq-amount-due">
                      ${accountInfo.subscription.amount} USD
                    </span>
                  </div>
                </>
              ) : (
                <p>No se pudo cargar la información de la suscripción</p>
              )}
            </div>
          </div>

          {/* Qué significa esto */}
          <div className="urbanq-info-section">
            <h3>¿Qué significa esto?</h3>
            <ul className="urbanq-info-list">
              <li>
                <FaCreditCard className="urbanq-list-icon" />
                <span>No podrás acceder a tu dashboard hasta que reactives tu suscripción</span>
              </li>
              <li>
                <FaExclamationTriangle className="urbanq-list-icon" />
                <span>Tus datos están seguros, pero no podrás crear nuevos registros</span>
              </li>
              <li>
                <FaCalendar className="urbanq-list-icon" />
                <span>Tienes 30 días para reactivar antes de que tu cuenta sea cancelada permanentemente</span>
              </li>
            </ul>
          </div>

          {/* Botones de acción */}
          <div className="urbanq-action-buttons">
            <button 
              className="urbanq-btn urbanq-btn-primary"
              onClick={handleReactivate}
            >
              <FaCreditCard />
              <span>Reactivar Suscripción</span>
            </button>
            
            <button 
              className="urbanq-btn urbanq-btn-secondary"
              onClick={handleContactSupport}
            >
              <FaEnvelope />
              <span>Contactar Soporte</span>
            </button>
          </div>

          {/* Información adicional */}
          <div className="urbanq-help-section">
            <p className="urbanq-help-text">
              <strong>¿Tienes problemas con el pago?</strong><br />
              Nuestro equipo de soporte está disponible para ayudarte.
            </p>
            <div className="urbanq-contact-info">
              <div className="urbanq-contact-item">
                <FaEnvelope />
                <span>soporte@urbanq.com</span>
              </div>
              <div className="urbanq-contact-item">
                <FaPhone />
                <span>+1 (555) 123-4567</span>
              </div>
            </div>
          </div>

          {/* Descuentos especiales */}
          {accountInfo?.discount && (
            <div className="urbanq-discount-banner">
              <div className="urbanq-discount-icon">🎁</div>
              <div className="urbanq-discount-content">
                <h4>¡Oferta Especial de Reactivación!</h4>
                <p>
                  Reactiva ahora y obtén <strong>{accountInfo.discount}% de descuento</strong> en tu próximo período
                </p>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AccountSuspended;
